import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import {
  API_MS_CREATE_SALE,
  API_MS_DELETE_SALE,
  API_MS_GET_SALES,
  API_MS_GET_SALE_BY_ID,
  API_MS_MY_COMMISSION_PLAN,
  API_MS_SALE_COMMISSION_PREVIEW,
  API_MS_SALE_STATS,
  API_MS_SUBMIT_SALE,
  API_MS_UPDATE_SALE,
} from "@/constants/apiEndpoints";
import type {
  ICommissionPreviewParams,
  ICommissionPreviewResult,
  ICreateSalePayload,
  IListSalesParams,
  IMyCommissionPlan,
  IMySaleStatsData,
  ISale,
  ISaleListItem,
  IUpdateSalePayload,
} from "@/types/(company)/sales.interface";

export const msSalesApi = createApi({
  reducerPath: "msSalesApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: ["msSales", "msSale", "msCommissionPlan"],
  endpoints: (builder) => ({
    msGetSales: builder.query<
      IApiPaginatedResponse<ISaleListItem[]>,
      IListSalesParams
    >({
      query: (params) => ({ url: API_MS_GET_SALES, params }),
      providesTags: ["msSales"],
    }),
    msGetSaleStats: builder.query<IApiResponse<IMySaleStatsData>, void>({
      query: () => ({ url: API_MS_SALE_STATS }),
      providesTags: ["msSales"],
    }),
    msGetSaleById: builder.query<IApiResponse<ISale>, number>({
      query: (id) => ({ url: API_MS_GET_SALE_BY_ID(id) }),
      providesTags: ["msSale"],
    }),

    msCreateSale: builder.mutation<IApiResponse<ISale>, ICreateSalePayload>({
      query: (body) => ({ url: API_MS_CREATE_SALE, method: "POST", body }),
      invalidatesTags: ["msSales"],
    }),
    msUpdateSale: builder.mutation<
      IApiResponse<ISale>,
      { id: number; payload: IUpdateSalePayload }
    >({
      query: ({ id, payload }) => ({
        url: API_MS_UPDATE_SALE(id),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["msSales", "msSale"],
    }),
    /** Send a draft — or a sale that came back with changes requested — for approval. */
    msSubmitSale: builder.mutation<IApiResponse<ISale>, number>({
      query: (id) => ({ url: API_MS_SUBMIT_SALE(id), method: "POST" }),
      invalidatesTags: ["msSales", "msSale"],
    }),
    msDeleteSale: builder.mutation<IApiResponse<{ deleted: boolean }>, number>({
      query: (id) => ({ url: API_MS_DELETE_SALE(id), method: "DELETE" }),
      invalidatesTags: ["msSales"],
    }),

    /**
     * The plan that currently applies to the signed-in employee, with the
     * assignment that matched — powers the "My Commission Plan" tab and the
     * live estimate in the log-sale form.
     */
    msGetMyCommissionPlan: builder.query<IApiResponse<IMyCommissionPlan>, void>({
      query: () => ({ url: API_MS_MY_COMMISSION_PLAN }),
      providesTags: ["msCommissionPlan"],
    }),

    /** Server-side commission calculation, used to confirm the UI's preview. */
    msPreviewCommission: builder.query<
      IApiResponse<ICommissionPreviewResult>,
      ICommissionPreviewParams
    >({
      query: (params) => ({ url: API_MS_SALE_COMMISSION_PREVIEW, params }),
    }),
  }),
});

export const {
  useMsGetSalesQuery,
  useMsGetSaleStatsQuery,
  useMsGetSaleByIdQuery,
  useMsCreateSaleMutation,
  useMsUpdateSaleMutation,
  useMsSubmitSaleMutation,
  useMsDeleteSaleMutation,
  useMsGetMyCommissionPlanQuery,
  useMsPreviewCommissionQuery,
} = msSalesApi;
